import {
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormikErrors } from "formik";
import { OptionType } from "../SelectInput";
import classNames from "classnames";
import { FaCaretDown } from "react-icons/fa6";
import { getSelectedOptionsLabel } from "./utils/funtions";
import { MultiSelectOptions } from "./components/MultiSelectOptions";

export type MultiSelectProps = Omit<
  InputHTMLAttributes<HTMLButtonElement>,
  "onChange"
> & {
  label?: string;
  error?: FormikErrors<FormData>;
  options: OptionType[];
  defaultOptionLabel?: string;
  selectedOptions?: string[];
  onChange?: (selectedOptions: string[]) => void;
};

export const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (props, _ref) => {
    const {
      label,
      error,
      options,
      defaultOptionLabel = "Selecciona una opción",
      selectedOptions = [],
      onChange,
    } = props;

    const [showOptions, setShowOptions] = useState(false);

    const selectedValueLabel = getSelectedOptionsLabel({
      options,
      selectedOptions,
      defaultOptionLabel,
    });

    const inputWrapperRef = useRef<HTMLDivElement>(null);

    const multiSelectClass = {
      wrapper: classNames("mb-2 relative"),
      input: classNames(
        "text-left border-slate-300 rounded p-2 border w-full h-11 relative",
        {
          "border-red-500": !!error,
        }
      ),
      inputTextWrapper: classNames(
        "pr-4 whitespace-nowrap overflow-hidden text-ellipsis"
      ),
      inputIcon: classNames("absolute right-3 top-0 bottom-0 m-auto"),
      error: classNames("error text-red-500"),
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          inputWrapperRef?.current &&
          !inputWrapperRef?.current.contains(event.target as Node)
        ) {
          setShowOptions(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [inputWrapperRef]);

    const handleToggleOptions = () => {
      setShowOptions((prev) => !prev);
    };

    const handleSelectOption = (value: string) => {
      const isSelected = selectedOptions?.includes(value);
      if (!isSelected) {
        onChange?.([...selectedOptions, value]);
        handleToggleOptions();
        return;
      }
      const newSelectedOptions = selectedOptions?.filter(
        (option) => option !== value
      );
      onChange?.(newSelectedOptions);
      handleToggleOptions();
    };

    return (
      <div ref={inputWrapperRef} className={multiSelectClass.wrapper}>
        <label className="block font-medium">{label}</label>
        <button
          onClick={handleToggleOptions}
          className={multiSelectClass.input}
          type="button"
        >
          <div className={multiSelectClass.inputTextWrapper}>
            <span>{selectedValueLabel}</span>
            <FaCaretDown className={multiSelectClass.inputIcon} />
          </div>
        </button>
        {showOptions && (
          <MultiSelectOptions
            options={options}
            selectedOptions={selectedOptions}
            onSelectOption={handleSelectOption}
          />
        )}

        {error && <div className={multiSelectClass.error}>{<>{error}</>}</div>}
      </div>
    );
  }
);
